import React from "react";

import CloseIcon from "@mui/icons-material/Close";
import LockIcon from "@mui/icons-material/Lock";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { usePlatformsSelect } from "store";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export const DataroomInfoConnect: React.FC<Props> = ({ isOpen, onClose }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const platforms = usePlatformsSelect();

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : "8px",
          width: "864px",
          maxWidth: "100%"
        }
      }}
    >
      <DialogTitle sx={{ p: "16px 24px" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography sx={{ fontSize: "18px", fontWeight: 600, color: "#071F35" }}>Connect to Data Room</Typography>
          <IconButton onClick={onClose} size="sm">
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
        <Divider sx={{ mt: "16px", borderColor: "#E5E8EF" }} />
      </DialogTitle>

      <DialogContent sx={{ p: "8px 24px 24px" }}>
        <Typography
          sx={{
            fontSize: "13px",
            color: "#496682",
            mb: "16px",
            fontStyle: "italic"
          }}
        >
          Please contact your Altius representative to enable cloud integration connectivity.
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: "12px",
            [theme.breakpoints.down("sm")]: {
              gridTemplateColumns: "repeat(2, 1fr)"
            }
          }}
        >
          {platforms?.map((platform) => (
            <Tooltip key={platform.id} title={platform.name} arrow placement="top">
              <Box
                sx={{
                  position: "relative",
                  borderRadius: "8px",
                  border: "1px solid #E5E8EF",
                  overflow: "hidden",
                  cursor: "not-allowed",
                  height: "155px",
                  display: "flex",
                  flexDirection: "column"
                }}
              >
                {/* Logo area */}
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: "14px"
                  }}
                >
                  <Box
                    component="img"
                    src={platform.logo}
                    alt={platform.name}
                    sx={{
                      maxWidth: "100%",
                      maxHeight: "80px",
                      objectFit: "contain"
                    }}
                  />
                </Box>

                {/* Divider */}
                <Box sx={{ px: "14px" }}>
                  <Divider sx={{ borderColor: "#E5E8EF" }} />
                </Box>

                {/* Footer */}
                <Box sx={{ px: "14px", py: "10px" }}>
                  <Typography
                    sx={{
                      fontSize: "13px",
                      color: "#071F35",
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis"
                    }}
                  >
                    {platform.name}
                  </Typography>
                </Box>

                {/* Lock overlay — covers entire card */}
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    backgroundColor: "rgba(18, 18, 23, 0.45)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  <LockIcon sx={{ color: "#FFFFFF", fontSize: 28 }} />
                </Box>
              </Box>
            </Tooltip>
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
};
